import {
  CountryOfficeCount,
  EmployeeLocation,
} from '@aibees/elephant/components/employees-trends-location';
import { Scrollbar, TableNoData } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Card, Stack } from '@mui/material';
import { memo, useEffect, useRef } from 'react';
import { useCompanyStatsContext } from '../../context';
import { CompanyStats } from '../../types';
import { EmployeeLocationCard } from '../employee-location-card';
import { OfficeLocationCard } from '../office-location-card';
import * as Styled from './styles';
import * as SX from './sx';
import type { CardWrapperProps as Props } from './types';

export const CardWrapper = memo(({ className }: Props) => {
  const { employees, offices, hoveredItem, statsType } =
    useCompanyStatsContext();

  const employeeRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const handleScrollToEmployee = () => {
    const element = hoveredItem ? employeeRefs.current[hoveredItem] : undefined;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useEffect(() => {
    handleScrollToEmployee();
  }, [hoveredItem]);

  const items = statsType === CompanyStats.Employee ? employees : offices;

  return (
    <Styled.Wrapper
      className={classnames('CardWrapper', className)}
      sx={SX.root}
    >
      {!items.length && (
        <Card sx={SX.box} variant="outlined">
          <TableNoData
            isError={false}
            config={{
              emptyIcon: 'location',
              title: 'No Data Available!',
              emptyIconWrapperSX: {
                padding: (p) => p.spacing(1.5, 1),
              },
            }}
          />
        </Card>
      )}
      {items.length > 0 && (
        <Scrollbar sx={SX.Scrollbar}>
          <Stack gap={1}>
            {items?.map((item) => (
              <Card
                key={item.key}
                ref={(el: HTMLDivElement | null) =>
                  (employeeRefs.current[item.key] = el)
                }
                variant="outlined"
                sx={SX.itemWrapper}
              >
                {statsType === CompanyStats.Employee && (
                  <EmployeeLocationCard
                    key={item.key}
                    item={item as EmployeeLocation}
                    hoveredItem={hoveredItem}
                  />
                )}
                {statsType === CompanyStats.Office && (
                  <OfficeLocationCard
                    key={item.key}
                    item={item as CountryOfficeCount}
                    hoveredItem={hoveredItem}
                  />
                )}
              </Card>
            ))}
          </Stack>
        </Scrollbar>
      )}
    </Styled.Wrapper>
  );
});

CardWrapper.displayName = 'CardWrapper';
